import React, { useEffect, useRef } from "react";
import { Eye, Loader2, Download } from "lucide-react";
import { downloadPolicyDocument } from "../services/dashboardService";

const RecentAnalysesTable = ({ data = [], onView, hasMore, loadingMore, onLoadMore }) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!sentinelRef.current || !onLoadMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore();
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore]);

  return (
    <div className="bg-white rounded-2xl border border-surface-border p-4 sm:p-6 flex flex-col gap-4">
      <h2 className="text-base font-bold text-brand-text">Recent Analysis</h2>
      <div className="overflow-x-auto -mx-1">
        <div className="overflow-y-auto max-h-[320px] pr-1" style={{ scrollbarWidth: "thin" }}>
          <table className="w-full text-sm min-w-[340px]">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-surface-border">
                <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4 whitespace-nowrap">Software Name</th>
                <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4 whitespace-nowrap">Privacy URL</th>
                <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4 whitespace-nowrap">Policy File</th>
                <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4 whitespace-nowrap">Date &amp; Time</th>
                <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4 whitespace-nowrap">Run Time</th>
                <th className="text-left text-xs font-medium text-brand-muted pb-3 w-full whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-sm text-brand-muted">
                    No recent analyses yet.
                  </td>
                </tr>
              ) : data.map((row, i) => (
                <tr key={row.assetId ?? i} className="border-b border-surface-border last:border-0">
                  <td className="py-3.5 pr-4">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-brand-text whitespace-nowrap">{row.company}</span>
                      {(row.versions?.length ?? 0) > 1 && (
                        <span className="text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full whitespace-nowrap">
                          {row.versions.length} versions
                        </span>
                      )}
                    </div>
                  </td>
                   <td className="py-3.5 pr-4">
                     <span className="block max-w-[260px] truncate text-brand-muted text-xs" title={row.url || ""}>{row.url || "—"}</span>
                   </td>
                  <td className="py-3.5 pr-4 whitespace-nowrap">
                    {row.document ? (
                      <button
                        onClick={() => downloadPolicyDocument(row.assetId, row.document.id, row.document.fileName)}
                        title={`Download ${row.document.fileName} (V${row.document.version})`}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline max-w-[160px]"
                      >
                        <Download className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{row.document.fileName}</span>
                        <span className="text-[10px] text-brand-muted shrink-0">V{row.document.version}</span>
                      </button>
                    ) : (
                      <span className="text-brand-muted text-xs">—</span>
                    )}
                  </td>
                  <td className="py-3.5 pr-4 whitespace-nowrap">
                    <div className="flex flex-col leading-tight">
                      <span className="text-brand-muted text-xs">{(row.date || "").split(" · ")[0]}</span>
                      <span className="text-brand-light text-[11px]">{(row.date || "").split(" · ")[1]}</span>
                    </div>
                  </td>
                  <td className="py-3.5 pr-4 text-brand-muted text-xs whitespace-nowrap">{row.runtime || "—"}</td>
                  <td className="py-3.5 text-left w-full whitespace-nowrap">
                    <button
                      onClick={() => onView && onView(row)}
                      className="inline-flex items-center gap-1.5 text-primary text-xs font-semibold hover:underline"
                    >
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                  </td>
                </tr>
              ))}

              {/* Scroll sentinel */}
              {hasMore && (
                <tr>
                  <td colSpan={6} className="py-2 text-center" ref={sentinelRef}>
                    {loadingMore && (
                      <Loader2 className="w-4 h-4 text-primary animate-spin inline-block" />
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RecentAnalysesTable;
